import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { adminApi } from "../api";
import { EmptyState, Panel } from "../components/DataTable";

export function SettingsPage() {
  const [email, setEmail] = useState("");
  const webhooks = useQuery({ queryKey: ["admin-webhooks"], queryFn: adminApi.webhooks, refetchInterval: 5000 });
  const testEmail = useMutation({ mutationFn: adminApi.testEmail });

  const pixelQuery = useQuery({ queryKey: ["admin-pixel-settings"], queryFn: adminApi.pixelSettings });
  const updatePixel = useMutation({
    mutationFn: adminApi.updatePixelSettings,
    onSuccess: () => pixelQuery.refetch()
  });

  const [conversionId, setConversionId] = useState("");
  const [purchaseLabel, setPurchaseLabel] = useState("");
  const [beginCheckoutLabel, setBeginCheckoutLabel] = useState("");
  const [addPaymentInfoLabel, setAddPaymentInfoLabel] = useState("");
  const [enhancedConversionsEnabled, setEnhancedConversionsEnabled] = useState(true);

  useEffect(() => {
    if (pixelQuery.data?.googleAds) {
      setConversionId(pixelQuery.data.googleAds.conversionId || "");
      setPurchaseLabel(pixelQuery.data.googleAds.purchaseLabel || "");
      setBeginCheckoutLabel(pixelQuery.data.googleAds.beginCheckoutLabel || "");
      setAddPaymentInfoLabel(pixelQuery.data.googleAds.addPaymentInfoLabel || "");
      setEnhancedConversionsEnabled(pixelQuery.data.googleAds.enhancedConversionsEnabled ?? true);
    }
  }, [pixelQuery.data]);

  const handleSavePixel = () => {
    updatePixel.mutate({
      googleAds: {
        conversionId,
        purchaseLabel,
        beginCheckoutLabel,
        addPaymentInfoLabel,
        enhancedConversionsEnabled
      }
    });
  };

  return (
    <div className="space-y-6">
      <Panel title="Google Ads Tracking & Pixel Settings">
        <p className="mb-4 text-xs text-muted font-normal">
          Configure o seu Google Ads Conversion ID (ex: <code>AW-123456789</code>) e os rótulos de conversão para rastreamento automático de atribuição cross-domain e Enhanced Conversions.
        </p>

        <div className="space-y-4 max-w-xl">
          <div>
            <label className="block text-xs font-semibold mb-1">Google Ads Conversion ID (Pixel)</label>
            <input
              className="field w-full"
              value={conversionId}
              onChange={(e) => setConversionId(e.target.value)}
              placeholder="AW-XXXXXXXXX"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold mb-1">Purchase Label (Conversão de Compra)</label>
              <input
                className="field w-full"
                value={purchaseLabel}
                onChange={(e) => setPurchaseLabel(e.target.value)}
                placeholder="Ex: abc_123xyz"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Begin Checkout Label (Opcional)</label>
              <input
                className="field w-full"
                value={beginCheckoutLabel}
                onChange={(e) => setBeginCheckoutLabel(e.target.value)}
                placeholder="Ex: def_456uvw"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Add Payment Info Label (Opcional)</label>
            <input
              className="field w-full"
              value={addPaymentInfoLabel}
              onChange={(e) => setAddPaymentInfoLabel(e.target.value)}
              placeholder="Ex: ghi_789rst"
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="enhanced_conv"
              checked={enhancedConversionsEnabled}
              onChange={(e) => setEnhancedConversionsEnabled(e.target.checked)}
              className="rounded text-brand focus:ring-brand"
            />
            <label htmlFor="enhanced_conv" className="text-xs font-medium cursor-pointer">
              Ativar Google Ads Enhanced Conversions (Conversões Avançadas com Email, Telefone e Endereço Hasheados)
            </label>
          </div>

          <div className="pt-2">
            <button
              className="btn btn-primary"
              onClick={handleSavePixel}
              disabled={updatePixel.isPending}
            >
              {updatePixel.isPending ? "Salvando..." : "Salvar Configurações do Google Ads"}
            </button>
            {updatePixel.isSuccess && (
              <span className="ml-3 text-xs text-trust font-medium">Configurações salvas com sucesso!</span>
            )}
          </div>
        </div>
      </Panel>

      <Panel title="Teste Resend">
        <div className="flex flex-wrap gap-2">
          <input className="field max-w-sm" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="destino@dominio.com" />
          <button className="btn" onClick={() => testEmail.mutate(email)} disabled={testEmail.isPending}>Enviar teste</button>
        </div>
        {testEmail.data && <p className="mt-3 text-sm text-trust">Email enviado: {testEmail.data.id}</p>}
        {testEmail.error && <p className="mt-3 text-sm text-red-600">{testEmail.error.message}</p>}
      </Panel>

      <Panel title="Webhooks recebidos">
        {!webhooks.data?.length ? <EmptyState text="Nenhum webhook registrado em memoria local." /> : (
          <ul className="space-y-2 text-sm">
            {webhooks.data.map((item) => <li key={item.id} className="rounded-md border border-line p-2 font-mono text-xs">{item.id}</li>)}
          </ul>
        )}
      </Panel>
    </div>
  );
}
